import React, { useEffect, useState, cloneElement, lazy, createElement, Component } from 'react';
import { MoonStarIcon, SmileIcon, SparklesIcon, WandIcon, ChevronDownIcon, ChevronUpIcon, PencilIcon } from 'lucide-react';
import { SummaryPopup } from './SummaryPopup';
import { LoadingBunny } from './LoadingBunny';
import { Customize } from './Customize';
import { useNavigate } from 'react-router-dom';
import { FaqItem } from './FaqItem';
import { Link } from 'react-router-dom';
import { SEOHead } from './SEOHead';
import { createWebsiteSchema, createOrganizationSchema, createSoftwareApplicationSchema, createServiceSchema } from '../utils/structuredData';
import { initializeReferralTracking } from '../utils/tracking';
type Mood = 'playground' | 'outerspace' | 'underwater';
type Topic = 'bunny' | 'dragon' | 'cupcake';
type HomePageProps = {
  onEasyCreate: () => void;
};
export const HomePage = ({
  onEasyCreate
}: HomePageProps) => {
  const navigate = useNavigate();
  // Add debouncing for script initialization
  useEffect(() => {
    const cleanup = initializeReferralTracking();
    return () => {
      if (cleanup) cleanup();
    };
  }, []);
  const [name, setName] = useState('Bunny'); // Changed from 'Story Bunny' to 'Bunny'
  const [selectedMood, setSelectedMood] = useState<Mood | null>(null);
  const [selectedTopics, setSelectedTopics] = useState<Topic[]>([]);
  const [showSummary, setShowSummary] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCustomize, setShowCustomize] = useState(false);
  const [customDetails, setCustomDetails] = useState<{
    storyText: string;
    artStyle?: string;
  }>({
    storyText: ''
  });
  const [storyText, setStoryText] = useState('');
  const [isRecording, setIsRecording] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [selectedAge, setSelectedAge] = useState<string | null>(null);
  const [selectedStyle, setSelectedStyle] = useState<string | null>(null);
  const [selectedStoryType, setSelectedStoryType] = useState<'classic' | 'silly' | 'adventure' | null>(null);
  const moods = [{
    id: 'playground',
    label: 'At Home',
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/jgXos4HGMJczR2iZbZBGqQ/ChatGPT_Image_Jun_21%2C_2025_at_12_42_33_AM.png'
  }, {
    id: 'outerspace',
    label: 'Underwater',
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/rxDwhHDKr3FVpowLPJfpsa/ChatGPT_Image_Jun_21%2C_2025_at_12_47_13_AM.png'
  }, {
    id: 'underwater',
    label: 'Somewhere Magical',
    icon: 'https://uploadthingy.s3.us-west-1.amazonaws.com/9RzsS1qb3QUQrpegi8SCk1/ChatGPT_Image_Jun_21%2C_2025_at_12_51_04_AM.png'
  }];
  const topics: {
    id: Topic;
    label: string;
    image: string;
  }[] = [{
    id: 'bunny',
    label: 'Animals',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/5TfiQPUnMvb7VjgrcQkriQ/ChatGPT_Image_Jun_20%2C_2025_at_11_50_09_PM.png'
  }, {
    id: 'dragon',
    label: 'Kids',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/tBh4h86ZSZq8ndVk2HPGX6/ChatGPT_Image_Jun_21%2C_2025_at_12_01_50_AM.png'
  }, {
    id: 'cupcake',
    label: 'Magical Creatures',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/wAYgnyLgDKfFLDRRvCKkHR/ChatGPT_Image_Jun_21%2C_2025_at_12_05_49_AM.png'
  }];
  const readingLevels = [{
    id: '2-3',
    label: 'Toddler\n(Ages 2-3)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/w4fBkhnJPJwVTcBqua2JrY/ChatGPT_Image_May_19%2C_2025_at_09_12_16_AM.png'
  }, {
    id: '4-5',
    label: 'Little Kid\n(Ages 4-5)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/7QLNgNsz5oE6LVGwUGcCCc/ChatGPT_Image_May_19%2C_2025_at_09_11_21_AM.png'
  }, {
    id: '6+',
    label: 'Big Kid\n(Ages 6+)',
    image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/pW1GB2jk9KBTNLntf4iwox/ChatGPT_Image_May_19%2C_2025_at_10_05_41_AM.png'
  }];
  const illustrationStyles = [{
    id: 'style1',
    label: 'Crayon',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/hJgG7WaMQVqX1ZZF28nUbs/ChatGPT_Image_May_23%2C_2025_at_05_12_41_PM.png'
  }, {
    id: 'style2',
    label: 'Fuzzy',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/mXtDC6Y4ajExAmGBGPoCpG/ChatGPT_Image_May_18%2C_2025_at_06_20_27_PM.png'
  }, {
    id: 'style3',
    label: 'Bright',
    url: 'https://uploadthingy.s3.us-west-1.amazonaws.com/up1qA9wkD8XpeMBGm7WTHa/ChatGPT_Image_May_18%2C_2025_at_06_47_32_PM.png'
  }];
  const toggleTopic = (topic: Topic) => {
    setSelectedTopics(prev => {
      if (prev.includes(topic)) {
        return prev.filter(t => t !== topic);
      }
      return [...prev, topic];
    });
  };
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setShowSummary(true);
  };
  const handleCreateStory = () => {
    setShowSummary(false);
    setIsLoading(true);
    // Removed the shorter timeout to let SummaryPopup control the timing
  };
  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Only take the first word (no spaces allowed) and capitalize first letter
    const singleWord = e.target.value.trim().split(' ')[0];
    const capitalizedWord = singleWord.charAt(0).toUpperCase() + singleWord.slice(1);
    setName(capitalizedWord);
  };
  const handleFocus = () => {
    if (name === 'Bunny') {
      setName('');
    }
  };
  const toggleRecording = () => {
    setIsRecording(!isRecording);
    setIsProcessing(true);
  };
  const handleRandomPrompt = () => {
    // Add logic for generating a random prompt
  };
  const formatTopics = (topics: typeof selectedTopics) => {
    if (!topics.length) return '';
    return topics.map(topic => topics.find(t => t.id === topic)?.label || topic).join(', ');
  };
  const generatePrompt = () => {
    const summaryText = [`Make a story about ${name}`, selectedTopics.length > 0 ? `with ${formatTopics(selectedTopics)}` : '', selectedMood ? `that takes place in ${moods.find(m => m.id === selectedMood)?.label.toLowerCase() || ''}` : '', selectedAge ? `The story is for a ${selectedAge} year old child.` : '', storyText ? `Background: ${storyText}` : ''].filter(Boolean).join('. ');
    return summaryText;
  };
  const structuredData = [createWebsiteSchema(), createOrganizationSchema(), createSoftwareApplicationSchema(), createServiceSchema()];
  const onStyleSelect = (styleLabel: string) => {
    setCustomDetails(prev => ({
      ...prev,
      artStyle: styleLabel
    }));
  };
  const handleImageError = (e: React.SyntheticEvent<HTMLImageElement, Event>) => {
    e.currentTarget.src = 'https://placehold.co/400x400?text=Story+Setting';
  };
  return <>
      <SEOHead title="Story Bunny - Create Magical AI Stories for Kids" description="Turn your child's imagination into beautifully illustrated storybooks! Story Bunny uses safe AI to create personalized stories for children ages 3-6. Start your storytelling adventure today." canonical="https://storybunny.ai/" structuredData={structuredData} ogType="website" data-id="element-439" />
      <div className="flex-1 flex flex-col bg-[#FFFAE4] overflow-hidden" data-id="element-440">
        {isLoading && <LoadingBunny data-id="element-441" />}
        {showSummary && <SummaryPopup name={name || ''} topics={selectedTopics.map(topic => topics.find(t => t.id === topic)?.label || topic)} mood={moods.find(m => m.id === selectedMood)?.label?.toLowerCase() || ''} storyText={customDetails.storyText} artStyle={customDetails.artStyle} selectedAge={selectedAge} selectedStoryType={selectedStoryType} onClose={() => setShowSummary(false)} onCreate={handleCreateStory} data-id="element-442" />}
        {/* New Header with Logo */}

        <div className="w-full max-w-lg mx-auto text-center px-6 min-h-screen flex flex-col justify-between pb-8" data-id="element-443">
          <div className="space-y-8 pt-4" data-id="element-444">
            {' '}
            {/* Main content container */}
            {/* Step 1: Name Input */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-445">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center justify-center" data-id="element-446">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-447">
                  1
                </span>
                What's your main character's name?
              </h2>
              <input type="text" value={name} onChange={handleNameChange} onFocus={handleFocus} placeholder="Bunny" className={`w-60 px-4 py-2 bg-[#FFFBF6] border-2 border-[#E5C1A9] rounded-xl text-base ${name === 'Bunny' ? 'text-[#6B4B3E]/50' : 'text-[#6B4B3E]'} placeholder-[#6B4B3E]/50 focus:outline-none focus:border-[#D4A088] leading-normal text-center mx-auto block`} data-id="element-448" />
            </div>
            {/* Step 2: Who Selection */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-449">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center justify-center" data-id="element-450">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-451">
                  2
                </span>
                What kind of characters do you want?
              </h2>
              <div className="grid grid-cols-3 gap-4" data-id="element-452">
                {topics.map(topic => <button key={topic.id} type="button" onClick={() => toggleTopic(topic.id)} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedTopics.includes(topic.id) ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-453">
                    <div className="w-full aspect-square relative" data-id="element-454">
                      <div className="w-full h-full flex items-center justify-center" data-id="element-455">
                        <img src={topic.image} alt={topic.label} className="max-w-[80%] max-h-[80%] object-contain" data-id="element-456" />
                      </div>
                    </div>
                    <span className="mt-2 font-medium text-sm" data-id="element-457">
                      {topic.label}
                    </span>
                  </button>)}
              </div>
            </div>
            {/* Step 3: Where Selection */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-458">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center justify-center" data-id="element-459">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-460">
                  3
                </span>
                Where does your story take place?
              </h2>
              <div className="grid grid-cols-3 gap-4" data-id="element-461">
                {moods.map(mood => <button key={mood.id} type="button" onClick={() => setSelectedMood(mood.id as Mood)} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedMood === mood.id ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-462">
                    <div className="w-full aspect-square relative" data-id="element-463">
                      <img src={mood.icon} alt={mood.label} className="max-w-[80%] max-h-[80%] object-contain" onError={handleImageError} loading="lazy" data-id="element-464" />
                    </div>
                    <span className="mt-2 font-medium text-sm" data-id="element-465">
                      {mood.label}
                    </span>
                  </button>)}
              </div>
            </div>
            {/* Step 4: What kind of story do you want? */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-466">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-467">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-468">
                  4
                </span>
                What kind of story do you want?
              </h2>
              <div className="grid grid-cols-3 gap-4" data-id="element-469">
                <button type="button" onClick={() => setSelectedStoryType('classic')} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedStoryType === 'classic' ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-470">
                  <div className="w-full aspect-square relative" data-id="element-471">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/1fhWb4zwB25J17kfHvrDMq/ChatGPT_Image_Jun_21%2C_2025_at_01_27_43_AM.png" alt="Classic & Cozy" className="max-w-[80%] max-h-[80%] object-contain mx-auto" data-id="element-472" />
                  </div>
                  <span className="mt-2 font-medium text-sm" data-id="element-473">
                    Classic & Cozy
                  </span>
                </button>
                <button type="button" onClick={() => setSelectedStoryType('silly')} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedStoryType === 'silly' ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-474">
                  <div className="w-full aspect-square relative" data-id="element-475">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/4HhSCef1wP2WLBrSmkyU7a/ChatGPT_Image_Jun_21%2C_2025_at_01_28_45_AM.png" alt="Silly & Surprising" className="max-w-[80%] max-h-[80%] object-contain mx-auto" data-id="element-476" />
                  </div>
                  <span className="mt-2 font-medium text-sm" data-id="element-477">
                    Silly & Surprising
                  </span>
                </button>
                <button type="button" onClick={() => setSelectedStoryType('adventure')} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedStoryType === 'adventure' ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-478">
                  <div className="w-full aspect-square relative" data-id="element-479">
                    <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/vhGTEhhFBUzuPB5hLq4pwv/ChatGPT_Image_Jun_21%2C_2025%2C_01_41_03_AM.png" alt="Big Adventure" className="max-w-[80%] max-h-[80%] object-contain mx-auto" data-id="element-480" />
                  </div>
                  <span className="mt-2 font-medium text-sm" data-id="element-481">
                    Big Adventure
                  </span>
                </button>
              </div>
            </div>
            {/* Step 5: Reading Level (previously Step 4) */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-482">
              <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] mb-6 flex items-center justify-center" data-id="element-483">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-484">
                  5
                </span>
                Reading Level
              </h2>
              <div className="grid grid-cols-3 gap-4" data-id="element-485">
                {readingLevels.map(level => <button key={level.id} type="button" onClick={() => setSelectedAge(level.label)} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all ${selectedAge === level.label ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-486">
                    <div className="w-full aspect-square relative" data-id="element-487">
                      <img src={level.image} alt={`${level.label} years`} className="w-full h-full object-contain" data-id="element-488" />
                    </div>
                    <span className="mt-2 font-medium text-sm whitespace-pre-line" data-id="element-489">
                      {level.label}
                    </span>
                  </button>)}
              </div>
            </div>
            {/* Step 6: What should the pictures look like? */}
            <div className="bg-transparent rounded-xl p-6 shadow-sm border border-[#E5C1A9] mb-8" data-id="element-490">
              <h2 className="text-xl font-bold text-[#6B4B3E] mb-6 flex items-center" data-id="element-491">
                <span className="w-8 h-8 bg-[#FFF0E6] rounded-full flex items-center justify-center text-[#D4A088] mr-2" data-id="element-492">
                  6
                </span>
                What should the pictures look like?
              </h2>
              <div className="overflow-x-auto -mx-2 px-2" data-id="element-493">
                <div className="flex gap-4" data-id="element-494">
                  {illustrationStyles.map(style => <button key={style.id} onClick={() => {
                  setSelectedStyle(style.id);
                  onStyleSelect(style.label);
                }} className={`flex flex-col items-center p-4 rounded-xl border-2 transition-all w-[140px] flex-shrink-0 ${selectedStyle === style.id ? 'bg-[#E5C1A9] border-[#D4A088] text-[#6B4B3E]' : 'bg-[#FFFBF6] border-[#E5C1A9] text-[#6B4B3E] hover:border-[#D4A088]'}`} data-id="element-495">
                      <div className="w-full aspect-square relative" data-id="element-496">
                        <div className="w-full h-full flex items-center justify-center" data-id="element-497">
                          <img src={style.url} alt={`${style.label} style`} className="w-full h-full object-contain" data-id="element-498" />
                        </div>
                      </div>
                      <span className="mt-2 font-medium text-sm" data-id="element-499">
                        {style.label}
                      </span>
                    </button>)}
                </div>
              </div>
            </div>
            {/* Step 7: Add a Little Magic (previously Step 6) */}
            <div className="bg-white rounded-xl p-4 shadow-sm relative overflow-hidden cursor-pointer hover:shadow-md transition-all duration-300" data-id="element-500">
              <div className="absolute inset-0 bg-gradient-to-r from-pink-100/80 via-purple-100/80 to-blue-100/80 animate-gradient pointer-events-none" data-id="element-501"></div>
              <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(255,255,255,0.9),transparent)] pointer-events-none" data-id="element-502"></div>
              {/* Sparkle animations */}
              <div className="absolute inset-0 pointer-events-none" data-id="element-503">
                <div className="absolute top-1/4 left-1/4 w-2 h-2 bg-yellow-200 rounded-full animate-ping" data-id="element-504"></div>
                <div className="absolute top-3/4 right-1/4 w-2 h-2 bg-purple-200 rounded-full animate-ping delay-300" data-id="element-505"></div>
                <div className="absolute top-1/2 left-3/4 w-2 h-2 bg-pink-200 rounded-full animate-ping delay-700" data-id="element-506"></div>
              </div>
              <div onClick={() => setShowCustomize(!showCustomize)} className="relative cursor-pointer" data-id="element-507">
                <h2 className="text-lg md:text-xl font-bold text-[#6B4B3E] relative flex items-center justify-center" data-id="element-508">
                  <span className="relative" data-id="element-509">
                    <span className="absolute -inset-1 bg-gradient-to-r from-[#FFE4D6] via-[#FFD1BB] to-[#FFE4D6] blur-xl opacity-75 animate-pulse" data-id="element-510"></span>
                    <span className="relative" data-id="element-511">
                      ✨7. Click to Customize Your Story✨
                    </span>
                  </span>
                </h2>
              </div>
              <div className={`relative z-10 transition-all duration-300 ease-in-out overflow-hidden ${showCustomize ? 'max-h-[2000px] opacity-100 mt-4' : 'max-h-0 opacity-0'}`} data-id="element-512">
                <Customize name={name} topics={selectedTopics.map(topic => topics.find(t => t.id === topic)?.label || topic)} mood={moods.find(m => m.id === selectedMood)?.label.toLowerCase() || ''} onUpdate={setCustomDetails} data-id="element-513" />
              </div>
            </div>
            {/* Create Button */}
            <button onClick={handleSubmit} disabled={!name || !selectedMood || selectedTopics.length === 0 || !selectedAge} className="px-12 py-6 bg-[#D4A088] text-white rounded-full text-2xl font-medium hover:bg-[#C28B73] transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center mx-auto mt-8" data-id="element-514">
              <SparklesIcon className="mr-3" size={32} data-id="element-515" />
              Click to Create
            </button>
          </div>
        </div>
        {/* A Safe Screen Time Tool Section */}
        <div className="w-full bg-[#FFF0E6] py-24" data-id="element-516">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-517">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-8 leading-tight" data-id="element-518">
              Every great story begins with an idea. Story Bunny is here to
              help!
            </h2>
            <p className="text-lg text-[#6B4B3E]/80 mb-16 leading-relaxed" data-id="element-519">
              Story Bunny helps young children turn their ideas into beautifully
              illustrated storybooks — powered by AI and guided by their
              imagination. It's a calm, ad-free space for creativity,
              storytelling, and self-expression — built to encourage kids to
              think, imagine, and create.
            </p>
            <div className="bg-[#FFFAE4] rounded-2xl p-8 shadow-sm border border-[#E5C1A9]" data-id="element-520">
              <p className="text-base text-[#6B4B3E]/80 italic mb-6" data-id="element-521">
                "My kids used to ask for the silliest bedtime stories — the ones
                we'd make up on the spot. Story Bunny brings that magic to life
                with a little help from today's technology, giving kids a fun
                way to create and share their own adventures."
              </p>
              <p className="text-[#6B4B3E] font-medium" data-id="element-522">
                — Grace Lee, Founder & Mom
              </p>
            </div>
          </div>
        </div>
        {/* Three Simple Steps Section */}
        <div className="w-full bg-[#FFFAE4] py-24" data-id="element-523">
          <div className="max-w-4xl mx-auto px-6 text-center" data-id="element-524">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-8 leading-tight" data-id="element-525">
              Three simple steps to make a story just for you.
            </h2>
            <p className="text-lg text-[#6B4B3E]/80 mb-16 leading-relaxed" data-id="element-526">
              We designed Story Bunny with kids and parents in mind. Kids can
              use it on their own to create stories. Parents can too! Story
              Bunny uses AI in the background to bring each idea to life.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-12 mb-16" data-id="element-527">
              {/* Step 1 */}
              <div className="flex flex-col items-center" data-id="element-528">
                <div className="w-16 h-16 bg-[#FFF0E6] rounded-full flex items-center justify-center text-2xl font-bold text-[#D4A088] mb-6" data-id="element-529">
                  1
                </div>
                <h3 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-530">
                  Start by adding a name
                </h3>
                <p className="text-[#6B4B3E]/80" data-id="element-531">
                  A simple way to customize your story from the very beginning.
                </p>
              </div>
              {/* Step 2 */}
              <div className="flex flex-col items-center" data-id="element-532">
                <div className="w-16 h-16 bg-[#FFF0E6] rounded-full flex items-center justify-center text-2xl font-bold text-[#D4A088] mb-6" data-id="element-533">
                  2
                </div>
                <h3 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-534">
                  Select your characters and setting
                </h3>
                <p className="text-[#6B4B3E]/80" data-id="element-535">
                  Choose who's in the story and where it takes place — every
                  choice leads to a different adventure.
                </p>
              </div>
              {/* Step 3 */}
              <div className="flex flex-col items-center" data-id="element-536">
                <div className="w-16 h-16 bg-[#FFF0E6] rounded-full flex items-center justify-center text-2xl font-bold text-[#D4A088] mb-6" data-id="element-537">
                  3
                </div>
                <h3 className="text-xl font-bold text-[#6B4B3E] mb-4" data-id="element-538">
                  Add an extra touch
                </h3>
                <p className="text-[#6B4B3E]/80" data-id="element-539">
                  Include a few details, like what happens next or how the
                  pictures should look. It's a fun way to shape the story in
                  your own words.
                </p>
              </div>
            </div>
            <a href="#top" className="inline-flex items-center justify-center px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors" data-id="element-540">
              Click to Create
            </a>
          </div>
        </div>
        {/* Why Parents Love Story Bunny Section */}
        <div className="w-full bg-[#FFF0E6] py-24" data-id="element-541">
          <div className="max-w-4xl mx-auto px-6" data-id="element-542">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-16 text-center" data-id="element-543">
              Why Parents Love Story Bunny
            </h2>
            {/* Founder Quote */}
            <div className="bg-[#FFFAE4] rounded-2xl p-8 shadow-sm border border-[#E5C1A9] mb-16" data-id="element-544">
              <p className="text-lg text-[#6B4B3E]/80 italic mb-8 leading-relaxed" data-id="element-545">
                "I love seeing kids use Story Bunny. It's so fun to see their
                expressions as they watch their ideas transform into an actual
                book, with beautiful illustrations and a storyline that's all
                their own. You'd also be surprised to see what grown-ups come up
                with too!
                <br data-id="element-546" />
                <br data-id="element-547" />
                What I love most is that the technology stays in the background.
                It's there to help — but what you're really seeing is the
                creativity of the person using it. Story Bunny makes it easy to
                go from an idea to a fully illustrated story.
                <br data-id="element-548" />
                <br data-id="element-549" />A spark of imagination is all it takes — Story Bunny makes
                it real."
              </p>
              <div className="flex items-center space-x-4" data-id="element-550">
                <div data-id="element-551">
                  <p className="text-[#6B4B3E] font-medium" data-id="element-552">
                    — Grace Lee Founder & Mom
                  </p>
                </div>
              </div>
            </div>
            {/* Benefits Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-553">
              {/* Benefit 1 */}
              <div className="bg-[#FFFAE4] rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-554">
                <div className="flex items-center space-x-3 mb-4" data-id="element-555">
                  <div className="text-2xl" data-id="element-556">✨</div>
                  <h3 className="text-xl font-bold text-[#6B4B3E]" data-id="element-557">
                    Creativity, Made Visible
                  </h3>
                </div>
                <p className="text-[#6B4B3E]/80" data-id="element-558">
                  Story Bunny helps kids turn their ideas into real stories —
                  with words, pictures, and characters they help choose. It's a
                  simple way to bring their imagination to life.
                </p>
              </div>
              {/* Benefit 2 */}
              <div className="bg-[#FFFAE4] rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-559">
                <div className="flex items-center space-x-3 mb-4" data-id="element-560">
                  <div className="text-2xl" data-id="element-561">✨</div>
                  <h3 className="text-xl font-bold text-[#6B4B3E]" data-id="element-562">
                    Effortless by Design
                  </h3>
                </div>
                <p className="text-[#6B4B3E]/80" data-id="element-563">
                  With Story Bunny, creating a story is simple from the start.
                  Whether it's a child using it on their own, a parent and child
                  working together, or a grown-up making something just for fun
                  — the process stays easy and intuitive.
                </p>
              </div>
              {/* Benefit 3 */}
              <div className="bg-[#FFFAE4] rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-564">
                <div className="flex items-center space-x-3 mb-4" data-id="element-565">
                  <div className="text-2xl" data-id="element-566">✨</div>
                  <h3 className="text-xl font-bold text-[#6B4B3E]" data-id="element-567">
                    A Gentle Way to Explore AI
                  </h3>
                </div>
                <p className="text-[#6B4B3E]/80" data-id="element-568">
                  Story Bunny uses AI in a creative, child-friendly way — not to
                  replace ideas, but to help shape and encourage them. It's a
                  thoughtful introduction to the AI technology they'll grow up
                  with.
                </p>
              </div>
              {/* Benefit 4 */}
              <div className="bg-[#FFFAE4] rounded-xl p-6 shadow-sm border border-[#E5C1A9]" data-id="element-569">
                <div className="flex items-center space-x-3 mb-4" data-id="element-570">
                  <div className="text-2xl" data-id="element-571">✨</div>
                  <h3 className="text-xl font-bold text-[#6B4B3E]" data-id="element-572">
                    A New Story Every Time
                  </h3>
                </div>
                <p className="text-[#6B4B3E]/80" data-id="element-573">
                  The fun is in the mix! Story Bunny uses your characters,
                  settings and ideas to create an actual book....surprises
                  guaranteed!
                </p>
              </div>
            </div>
          </div>
        </div>
        {/* FAQ Section */}
        <div className="w-full bg-[#FFFAE4] py-24" data-id="element-574">
          <div className="max-w-4xl mx-auto px-6" data-id="element-575">
            <h2 className="text-2xl font-bold text-[#6B4B3E] mb-16 text-center" data-id="element-576">
              Frequently Asked Questions
            </h2>
            {/* Getting Started FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-577">
              <FaqItem question="What ages is Story Bunny best for?" answer="Story Bunny is designed for children ages 3 to 6 — but anyone can enjoy using it." data-id="element-578" />
              <FaqItem question="Does my child need to know how to read or write?" answer="No. The interface is simple and intuitive. With just a few taps, your child can create a story from start to finish." data-id="element-579" />
              <FaqItem question="Can kids use it on their own?" answer="Yes. Story Bunny was made to be easy enough for kids to use independently — or as something fun to do together." data-id="element-580" />
              <FaqItem question="What devices does it work on?" answer="Story Bunny runs in your browser on any device — phone, tablet, or computer. No app or installation required." data-id="element-581" />
            </div>
            {/* AI & Technology FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-582">
              <FaqItem question="Does Story Bunny use AI?" answer="Yes. Story Bunny uses AI to generate the words and images in each story, based on your child's choices." data-id="element-583" />
              <FaqItem question="Is my child chatting with AI?" answer="No. Story Bunny doesn't use chat or conversation features." data-id="element-584" />
              <FaqItem question="How are the illustrations made?" answer="The artwork is created using generative AI — each image is unique and matched to the story your child creates." data-id="element-585" />
            </div>
            {/* Account & Privacy FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-586">
              <FaqItem question="Do I need to create an account?" answer="Yes — you'll need to create a free account to read your first story." data-id="element-587" />
              <FaqItem question="Will the story be saved?" answer="When you're logged in, your story is automatically saved as you go." data-id="element-588" />
              <FaqItem question="What data do you collect?" answer="We don't collect any personal data unless you create an account. We do collect anonymous usage data to help us improve the experience." data-id="element-589" />
            </div>
            {/* Story Creation FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-590">
              <FaqItem question="Are the stories appropriate for all ages?" answer="Yes. Story Bunny is designed with young children in mind, and stories are always age-appropriate and child-friendly." data-id="element-591" />
              <FaqItem question="Can I choose what kind of story it makes?" answer="Yes — you can choose the characters, setting, art style, and even describe what happens." data-id="element-592" />
              <FaqItem question="Can I print or download a story?" answer="Not at this time." data-id="element-593" />
            </div>
            {/* Technical FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-594">
              <FaqItem question="Is this an app?" answer="No — Story Bunny is browser-based. Just open it and start creating." data-id="element-595" />
              <FaqItem question="Can I lock the screen while my child is using it?" answer={<>
                    Yes —{' '}
                    <Link to="/lock-screen" className="text-[#D4A088] hover:underline" data-id="element-597">
                      view our step-by-step instructions
                    </Link>{' '}
                    to help you keep the page open and safe for solo use.
                  </>} data-id="element-596" />
            </div>
            {/* Pricing FAQ Group */}
            <div className="space-y-4 mb-12" data-id="element-598">
              <FaqItem question="How much does Story Bunny cost?" answer="It's free to create your first story. After that, it's $7/month for 10 stories, or $1 per additional story." data-id="element-599" />
              <FaqItem question="What's included in a subscription?" answer="A subscription includes 10 stories per month and access to your story library." data-id="element-600" />
              <FaqItem question="Can I use Story Bunny without a subscription?" answer="Yes — you can buy individual story credits anytime." data-id="element-601" />
            </div>
            {/* Still Curious CTA */}
            <div className="text-center mt-16" data-id="element-602">
              <p className="text-xl text-[#6B4B3E] mb-6" data-id="element-603">Still curious?</p>
              <p className="text-lg text-[#6B4B3E]/80 mb-8" data-id="element-604">
                The best way to learn is to try it.
              </p>
              <a href="#top" className="inline-flex items-center justify-center px-8 py-4 bg-[#D4A088] text-white rounded-full text-lg font-medium hover:bg-[#C28B73] transition-colors" data-id="element-605">
                Create Your Story
              </a>
            </div>
          </div>
        </div>
        {/* Decorative Bunnies */}
        <div className="fixed bottom-0 left-0 w-12 h-12 md:w-24 md:h-24 p-1 md:p-2 cursor-pointer" onDoubleClick={() => navigate('/dashboard')} data-id="element-606">
          <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/dB77smJQPVKBR7B7ysjQAn/ChatGPT_Image_May_17%2C_2025_at_10_47_42_AM.png" alt="Decorative bunny" className="w-full h-full object-contain transform -rotate-12 hover:scale-110 transition-transform" data-id="element-607" />
        </div>
        <div className="fixed bottom-0 right-0 w-12 h-12 md:w-24 md:h-24 p-1 md:p-2" data-id="element-608">
          <Link to="/faq" data-id="element-609">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/pbd7vAaVMr4hkiSxyRVnfj/ChatGPT_Image_May_17%2C_2025_at_10_41_15_AM.png" alt="Decorative bunny with question mark" className="w-full h-full object-contain transform rotate-12 hover:scale-110 transition-transform cursor-pointer" data-id="element-610" />
          </Link>
        </div>
      </div>
    </>;
};
<style jsx data-id="element-611">{`
  @keyframes gradient {
    0% {
      background-position: 0% 50%;
    }
    50% {
      background-position: 100% 50%;
    }
    100% {
      background-position: 0% 50%;
    }
  }
  .animate-gradient {
    background-size: 200% 200%;
    animation: gradient 8s ease infinite;
  }
`}</style>;